import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useGoogleLogin } from "@react-oauth/google";
import { loginRequest } from "./authConfig";
import { handleGoogleCredential } from "./googleAuth";
import axios from "axios";
import { jwtDecode } from "jwt-decode";

export default function SignIn({ onLogin }) {
  const { instance } = useMsal();
  const [error, setError] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [loading, setLoading] = useState(null); // "microsoft" | "google" | null

  const placeholder =
    "https://images.unsplash.com/photo-1688728775662-96d3be7b9554?auto=format&fit=crop&w=100&q=10&blur=20";
  const fullImage =
    "https://images.unsplash.com/photo-1688728775662-96d3be7b9554?auto=format&fit=crop&w=1400&q=80";

  // ── Microsoft ──────────────────────────────────────────────
  const handleMicrosoftLogin = async () => {
    setError(null);
    setLoading("microsoft");
    try {
      const loginResponse = await instance.loginPopup(loginRequest);
      const { idToken } = loginResponse;
      const decodedToken = jwtDecode(idToken);
      const tenantId = decodedToken.tid;
      localStorage.setItem("tenantId", tenantId);
      localStorage.setItem("authProvider", "microsoft");

      const response = await axios.post(
        "https://rentdoc-a0d223e1499e.herokuapp.com/api/auth/login",
        { idToken, tenantId },
        { withCredentials: true },
      );

      if (response.status === 200) {
        const { user, token } = response.data;
        localStorage.setItem("token", token);
        localStorage.setItem("tenantId", tenantId);
        onLogin(user);
      }
    } catch (e) {
      console.error("Microsoft login error:", e);
      setError(
        e.response?.data?.message ||
          "Microsoft login failed. Please try again.",
      );
    } finally {
      setLoading(null);
    }
  };

  // ── Google ─────────────────────────────────────────────────
  // useGoogleLogin uses the authorization code flow (more secure than credential callback)
  // In SignIn.jsx — your Google onSuccess handler
  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setError(null);
      setLoading("google");
      try {
        const response = await axios.post(
          "https://rentdoc-a0d223e1499e.herokuapp.com/api/auth/login-google",
          { accessToken: tokenResponse.access_token },
          { withCredentials: true },
        );

        console.log("Google response:", response.data); // Add this to see what's coming back

        if (response.data.success) {
          const { user, token } = response.data;
          localStorage.setItem("token", token);
          localStorage.setItem("authProvider", "google");
          onLogin(user); // ← this should fire
        } else {
          throw new Error(response.data.message || "Google login failed");
        }
      } catch (e) {
        console.error("Google login error:", e);
        setError(
          e.response?.data?.message || "Google login failed. Please try again.",
        );
      } finally {
        setLoading(null);
      }
    },
    onError: (err) => {
      console.error("Google OAuth error:", err);
      setError("Google sign-in was cancelled or failed.");
      setLoading(null);
    },
    flow: "implicit",
  });

  return (
    <div
      className="relative min-h-screen bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `url(${imageLoaded ? fullImage : placeholder})`,
      }}
    >
      <img
        src={fullImage}
        alt=""
        className="hidden"
        onLoad={() => setImageLoaded(true)}
      />
      <div className="absolute inset-0 bg-black opacity-50" />
      <div className="relative flex items-center justify-center min-h-screen">
        <div className="bg-white rounded-lg p-8 shadow-lg text-center w-80">
          <h1 className="text-2xl font-bold mb-6">Rent Doc</h1>

          <div className="flex flex-col gap-3">
            {/* Microsoft */}
            <button
              onClick={handleMicrosoftLogin}
              disabled={!!loading}
              className="flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
            >
              <img
                src="/Microsoft_logo.png"
                alt="Microsoft"
                className="h-5 w-5"
              />
              {loading === "microsoft"
                ? "Signing in…"
                : "Sign in with Microsoft"}
            </button>

            {/* Google */}
            <button
              onClick={() => handleGoogleLogin()}
              disabled={!!loading}
              className="flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
            >
              <GoogleIcon />
              {loading === "google" ? "Signing in…" : "Sign in with Google"}
            </button>
          </div>

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
          <p className="mt-4 text-sm text-gray-500">Sign in to manage leases</p>
        </div>
      </div>
    </div>
  );
}

// Inline SVG so you don't need an extra asset
function GoogleIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24">
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
  );
}
